import { Injectable, inject } from '@angular/core';
import { MainPicture, NewMainPicture } from '../models/mainPicture';
import { Product } from '../models/product';
import { Category } from '../models/category';
import { Section } from '../models/section';
import { APIService } from './api.service';
import { Deferred } from '../helpers/deferred';
import { ConfigService } from './config.service';
import { Letter } from '../models/config';

export interface FrontPageProduct extends Product {
  routerLink?: string;
  categoryUrlName: string;
}

export interface ProductResult extends Product {
  resultHtml?: string;
  text?: string;
}

export interface FrontPageSection extends Section {
  products: FrontPageProduct[];
  selectedProduct?: FrontPageProduct;
  numCategories: number;
}

@Injectable({
  providedIn: 'root',
})
export class FrontPageService {
  private readonly APIService = inject(APIService);
  private readonly ConfigService = inject(ConfigService);

  protected CACHE_TIME: number = 60 * 60 * 1000; //1 Hour

  protected sections: FrontPageSection[] = [];
  protected cacheTime_sections: number = 0;

  protected sectionPlaceholders: FrontPageSection[] = [];
  protected cacheTime_sectionPlaceholders: number = 0;

  protected mainPictures: MainPicture[] = [];
  protected cacheTime_mainPictures: number = 0;

  expireCache() {
    this.cacheTime_mainPictures = 0;
    this.cacheTime_sections = 0;
    this.cacheTime_sectionPlaceholders = 0;
  }

  getSectionPlaceholders() {
    const result = new Deferred<FrontPageSection[]>();

    if (
      this.sectionPlaceholders !== null &&
      this.cacheTime_sectionPlaceholders + this.APIService.getCacheTime(this.CACHE_TIME) > new Date().getTime()
    ) {
      result.resolve(this.sectionPlaceholders);
    } else {
      this.APIService.queueRequest<FrontPageSection[]>({
        endPoint: 'home',
        method: 'lowestLevelSections',
      })
        .then((sections: FrontPageSection[]) => {
          this.sectionPlaceholders = sections;
          this.cacheTime_sectionPlaceholders = new Date().getTime();
          result.resolve(this.sectionPlaceholders);
        })
        .catch((reason: unknown) => {
          result.reject(reason);
          //TODO should do something globally ?
        });
    }

    return result.promise;
  }

  getSections() {
    const result = new Deferred<FrontPageSection[]>();

    if (
      this.sections !== null &&
      this.cacheTime_sections + this.APIService.getCacheTime(this.CACHE_TIME) > new Date().getTime()
    ) {
      result.resolve(this.sections);
    } else {
      this.APIService.queueRequest<FrontPageSection[]>({
        endPoint: 'home',
        method: 'sectionProducts',
      })
        .then((sections: FrontPageSection[]) => {
          const promises: Array<Promise<unknown>> = [];

          for (const section of sections) {
            for (const product of section.products) {
              product.routerLink = '/products/' + section.urlName + '/';

              if (!section.alphabetic) {
                if (section.numCategories > 1) {
                  product.routerLink += product.categoryUrlName;
                }
              } else {
                promises.push(
                  this.ConfigService.matchLetter(product.productName).then((letter: Letter) => {
                    product.routerLink += letter.display;
                  }),
                );
              }
            }
          }

          this.APIService.processRequests();

          Promise.all(promises).then(() => {
            this.sections.splice(0, this.sections.length);
            Array.prototype.push.apply(this.sections, sections);
            this.cacheTime_sections = new Date().getTime();

            result.resolve(this.sections);
          });
        })
        .catch((reason: unknown) => {
          result.reject(reason);
          //TODO should do something globally ?
        });
    }

    return result.promise;
  }

  getMainPictures() {
    const result = new Deferred<MainPicture[]>();

    if (
      this.mainPictures !== null &&
      this.cacheTime_mainPictures + this.APIService.getCacheTime(this.CACHE_TIME) > new Date().getTime()
    ) {
      result.resolve(this.mainPictures);
    } else {
      this.APIService.queueRequest<MainPicture[]>({
        endPoint: 'home',
        method: 'mainPictures',
      })
        .then((mainPictures: MainPicture[]) => {
          for (const mainPicture of mainPictures) {
            mainPicture.imgSrc = '/images/dynamic/frontPage/' + mainPicture.mainPictureID;
          }

          this.mainPictures.splice(0, this.mainPictures.length);
          Array.prototype.push.apply(this.mainPictures, mainPictures);
          this.cacheTime_mainPictures = new Date().getTime();

          result.resolve(this.mainPictures);
        })
        .catch((reason: unknown) => {
          result.reject(reason);
          //TODO should do something globally ?
        });
    }

    return result.promise;
  }

  addProduct(product: Product, category: Category) {
    const deferred = new Deferred<boolean | string>();

    this.APIService.queueRequest<boolean | string>({
      //TODO error message ?
      endPoint: 'home',
      method: 'addSectionProduct',
      data: {
        productID: product.productID,
        categoryID: category.categoryID,
      },
    })
      .then((result: boolean | string) => {
        this.expireCache();
        deferred.resolve(result);
      })
      .catch((reason: unknown) => {
        deferred.reject(reason);
        //TODO should do something globally ?
      });

    return deferred.promise;
  }

  removeProduct(product: Product, section: Section) {
    const deferred = new Deferred<boolean>();

    this.APIService.queueRequest<boolean>({
      //TODO error message ?
      endPoint: 'home',
      method: 'removeSectionProduct',
      data: {
        productID: product.productID,
        sectionID: section.sectionID,
      },
    })
      .then((result: boolean) => {
        this.expireCache();
        deferred.resolve(result);
      })
      .catch((reason: unknown) => {
        deferred.reject(reason);
        //TODO should do something globally ?
      });

    return deferred.promise;
  }

  autocomplete(searchText: string, section: Section) {
    const deferred = new Deferred<ProductResult[]>();

    this.APIService.queueRequest<ProductResult[]>({
      //TODO error message ?
      endPoint: 'home',
      method: 'autocomplete',
      data: {
        searchText: searchText,
        sectionID: section.sectionID,
      },
    })
      .then((results: ProductResult[]) => {
        deferred.resolve(results);
      })
      .catch((reason: unknown) => {
        deferred.reject(reason);
        //TODO should do something globally ?
      });

    return deferred.promise;
  }

  categoriesOf(product: Product, section: Section) {
    const deferred = new Deferred<Category[]>();

    this.APIService.queueRequest<Category[]>({
      //TODO error message ?
      endPoint: 'home',
      method: 'categories',
      data: {
        productID: product.productID,
        sectionID: section.sectionID,
      },
    })
      .then((results: Category[]) => {
        deferred.resolve(results);
      })
      .catch((reason: unknown) => {
        deferred.reject(reason);
        //TODO should do something globally ?
      });

    return deferred.promise;
  }

  saveMainPicture(mainPicture: NewMainPicture) {
    const deferred = new Deferred<number>();

    this.APIService.queueRequest<number>({
      //TODO error message ?
      endPoint: 'home',
      method: 'saveMainPicture',
      data: {
        mainPictureID: mainPicture.mainPictureID,
        linkUrl: mainPicture.linkUrl,
        uploadID: mainPicture.uploadID,
      },
    })
      .then((mainPictureID: number) => {
        this.expireCache();
        deferred.resolve(mainPictureID);
      })
      .catch((reason: unknown) => {
        deferred.reject(reason);
        //TODO should do something globally ?
      });

    return deferred.promise;
  }

  removeMainPicture(mainPictureID: number) {
    const deferred = new Deferred<boolean>();

    this.APIService.queueRequest<boolean>({
      //TODO error message ?
      endPoint: 'home',
      method: 'removeMainPicture',
      data: {
        mainPictureID,
      },
    })
      .then((result: boolean) => {
        this.expireCache();
        deferred.resolve(result);
      })
      .catch((reason: unknown) => {
        deferred.reject(reason);
        //TODO should do something globally ?
      });

    return deferred.promise;
  }
}
